/**
 * Message cache
 *
 * The conversation list and the newest page of each conversation, kept in
 * IndexedDB so the messenger opens on what the user last saw instead of a
 * spinner, and still reads offline. It is a stale-while-revalidate cache:
 * the chat shows the cached copy at once and replaces it the moment the
 * server answers, so the cache only ever decides what the first frame shows.
 *
 * IndexedDB is per origin, not per account, so the store remembers whose
 * data it holds and refuses to hand one user's chats to the next one who
 * signs in on the same browser. Every call is best-effort: a browser without
 * IndexedDB (private mode, an old WebView) just gets no head start.
 */

import {
  clearAllData,
  getAllConversations,
  getMessagesByConversation,
  getUserData,
  replaceConversations,
  replaceMessagesForConversation,
  saveUserData,
} from '../pwa/offlineStorage';

const OWNER_KEY = 'messageCacheOwner';

/** How many of the newest messages to keep per conversation. */
export const CACHED_MESSAGES_PER_CONVERSATION = 30;

let ownerCheck = null;

/**
 * Resolves once the store is known to belong to `username`, wiping it first
 * if it belonged to somebody else. Resolves false when the store is unusable.
 */
const ensureOwner = (username) => {
  if (!username) return Promise.resolve(false);
  if (!ownerCheck || ownerCheck.username !== username) {
    const promise = (async () => {
      try {
        const owner = await getUserData(OWNER_KEY);
        if (owner !== username) {
          await clearAllData();
          await saveUserData(OWNER_KEY, username);
        }
        return true;
      } catch {
        return false;
      }
    })();
    ownerCheck = { username, promise };
  }
  return ownerCheck.promise;
};

// Local blob URLs on optimistic messages die with the page; a cached copy of
// one would render a broken image, so only what the server confirmed is kept.
const persistable = (message) => message && !message.isOptimistic && message.id != null;

const byCreatedAt = (a, b) => new Date(a.createdAt) - new Date(b.createdAt);

const byLastMessage = (a, b) => {
  const left = a.lastMessageCreatedAt ? new Date(a.lastMessageCreatedAt).getTime() : -Infinity;
  const right = b.lastMessageCreatedAt ? new Date(b.lastMessageCreatedAt).getTime() : -Infinity;
  return right - left;
};

/** The cached conversation list, newest activity first, or null. */
export const readCachedConversations = async (username) => {
  if (!(await ensureOwner(username))) return null;
  try {
    const conversations = await getAllConversations();
    return conversations.length ? conversations.sort(byLastMessage) : null;
  } catch {
    return null;
  }
};

export const writeCachedConversations = async (username, conversations) => {
  if (!Array.isArray(conversations) || !(await ensureOwner(username))) return;
  try {
    await replaceConversations(conversations);
  } catch {
    // Quota or a closed database: the next open just starts cold.
  }
};

/** The cached newest page of a conversation, oldest first, or null. */
export const readCachedMessages = async (username, conversationId) => {
  if (!conversationId || !(await ensureOwner(username))) return null;
  try {
    const messages = await getMessagesByConversation(conversationId);
    return messages.length ? messages.sort(byCreatedAt) : null;
  } catch {
    return null;
  }
};

/**
 * Remember the newest messages of a conversation. Pass the whole list the
 * screen holds; only the tail that fits the page size is kept.
 */
export const writeCachedMessages = async (username, conversationId, messages) => {
  if (!conversationId || !Array.isArray(messages) || !(await ensureOwner(username))) return;
  const tail = messages.filter(persistable).slice(-CACHED_MESSAGES_PER_CONVERSATION);
  try {
    await replaceMessagesForConversation(conversationId, tail);
  } catch {
    // Same as above: a lost write costs the next open its head start, no more.
  }
};

/** Forget everything, including whose data it was. Called on logout. */
export const clearMessageCache = async () => {
  ownerCheck = null;
  try {
    await clearAllData();
  } catch {
    // Nothing to clear, or nowhere to clear it from.
  }
};
